import { useEffect, useState } from 'react'
import { Button, Field, Input, Modal, Textarea } from '@/components/ui'
import { SmartImage } from '@/components/SmartImage'

/**
 * 分镜脚本里的一行。
 *
 * `t`（时间位）**只读**、`d`（画面描述）可改 —— 这个不对称是刻意的：
 * 时间位必须首尾相接、且加总等于成片时长（`smoke-prompts` 逐条核对，
 * 对不上就是「脚本说自己 16 秒」的假模板），把时间位交给用户手改，
 * 等于把这条硬约束改成「靠他记得」。而画面描述本来就是内容，本来就该能改。
 * 所以编辑区只开 `d` 这一扇门。
 */
export type TemplateShotLine = { t: string; d: string }

/**
 * 弹窗收集的五个字段 —— 其余（五个参数 / 封面 / 成片）都来自调用方。
 *
 * 后两项（`shots` / `shot`）是 2026-10-08 加的，用户口径
 * 「**提示词模板应该是可以编辑的** … 现在太简陋了」：
 * 模板里跟提示词有关的文字**只有这两块**（分镜的画面描述、跨全片约束），
 * 原来弹窗只收名字/角标/说明，于是「这条模板拍出来是什么样」在保存那一刻
 * 一个字都改不了 —— 用户看着一堆从当前表单抄过来的通用描述，只能先存了再说。
 */
export type TemplateFormValues = {
  name: string
  tag: string
  description: string
  /** 模板内容 · 分镜脚本（逐镜头的画面描述） */
  shots: TemplateShotLine[]
  /** 模板内容 · 跨全片约束（会被拼进提示词【补充】段的**那一行**） */
  shot: string
}

/**
 * 模板表单弹窗 —— **一处实现，两种用法**（2026-10-08）。
 *
 *   · `mode="create"`：工作台里的「存为模板」（用户口径「用户改完模板可以存储起来」）；
 *   · `mode="edit"`：模板中心里编辑自己那一条。
 *
 * 为什么合成一个而不是写两个弹窗：两种用法收的就是**同一组字段**，
 * 分两个文件的话，字段校验（名字不能空、trim、失败留在框里）与「打开时重置」
 * 这两条逻辑必然各写一遍 —— 时间一长就会出现「编辑时允许空名字、新建时不允许」
 * 这种不一致。差别只有三处，都由 `mode` 派生：标题、提交键文案、
 * 以及下面那块**核对区**（只有新建时才有意义 —— 编辑时参数与封面早就存好了，
 * 摆出来只是噪音）。
 *
 * ## 它只收集字段，参数 / 封面 / 成片全部来自当前表单
 *
 * 那几项都由调用方（`VideoStudio` / `TemplateGallery`）抽好交过来 ——
 * 让弹窗自己去读表单是错的：那样「保存的是哪一套」就取决于弹窗挂载时
 * 表单长什么样，而用户完全可能开着弹窗去改了参数（同一个页面上就能改）。
 *
 * ## 🔴 两块内容是**文字**，不是结构
 *
 * 弹窗里能改的只有「每一镜拍什么」（`shots[].d`）与「全片不许破的那几条」
 * （`shot`），**镜头数与时间轴不给改**（理由见 `TemplateShotLine` 的注释）。
 * 所以这里做的是一个**文本编辑器**，不是一个分镜排布器 —— 保存下去的
 * `storyboard` 结构仍然由调用方给的那份决定，这里只把描述替换掉。
 *
 * ## 为什么不对着 `onSubmit` 的返回值做状态
 *
 * 提交成功后由调用方更新清单并给 toast，弹窗只管**关掉自己**。
 * 弹窗里再放一份「保存成功」的提示，就是同一件事说两遍 ——
 * 而且 toast 会飘在页面右上角，用户关掉弹窗正好看到它。
 * 失败**必须留在弹窗里**报（不能吞）：这时弹窗里填的名字还在，
 * 用户改一个字就能重试，不用重填一遍。
 */
export function SaveTemplateDialog({
  open,
  onClose,
  mode = 'create',
  initial,
  coverUrl,
  detail,
  onSubmit,
  onAiShots,
  onAiShot,
}: {
  open: boolean
  onClose: () => void
  /** `create` = 新建（工作台）；`edit` = 编辑自己已存的模板（模板中心） */
  mode?: 'create' | 'edit'
  /** 各字段的初值（新建时一般是「产品名」或当前模板名 —— 空着最容易卡住） */
  initial: TemplateFormValues
  /** 封面。新建时 = 手上那张 3×3 分镜图；编辑时 = 这条模板已存的封面。空串 = 没有图 */
  coverUrl: string
  /**
   * 新建专用的核对数据（「存的是不是他现在这一套」）。编辑时**不要传** ——
   * 那块内容（参数 / 分镜数 / 封面来源 / 成片来源）说的是「这次会存下什么」，
   * 而编辑时那几项一个都没变。
   *
   * ⚠️ 这里**没有 `shotCount`**：镜头数从 `initial.shots.length` 现算。
   * 由调用方另传一份的话就是同一件事两处存，改了弹窗里的镜头就得记得同步它。
   */
  detail?: { summary: string; hasSample: boolean }
  /** 落库。抛错 = 失败，错误消息会显示在弹窗里 */
  onSubmit: (values: TemplateFormValues) => Promise<unknown>
  /**
   * 分镜「AI 扩写」：传当前这一版镜头进来，返回改写后的同一批镜头
   * （**段数必须一致** —— 段数对不上该由回调抛错，弹窗这边不覆盖）。
   *
   * **不传 = 不渲染那颗按钮。** 编辑已有模板时没有「这支片子拍什么」的上下文
   * （模板中心那页没有表单），硬给一颗按钮只会让它写出一份和产品无关的分镜。
   */
  onAiShots?: (shots: TemplateShotLine[]) => Promise<TemplateShotLine[]>
  /** 全片约束「AI 扩写」：传当前这段文字，返回改写后的一段（**单行**）。同上，不传不渲染 */
  onAiShot?: (shot: string) => Promise<string>
}) {
  const isEdit = mode === 'edit'
  const [name, setName] = useState(initial.name)
  /*
   * 角标**默认留空**（2026-10-08）。工作台的模板卡只有 84px 宽，
   * 「我的模板 · 」这 7 个字符是白吃掉的 —— 用户真正用来区分自己那几条的名字
   * 反而被两行截断。空角标时 `templateTitle()` 只返回名字（见那边的注释）。
   * 保留这个输入框是因为填了确实有用（自己按品分组），只是不该默认占位。
   */
  const [tag, setTag] = useState(initial.tag)
  const [description, setDescription] = useState(initial.description)
  /** 模板内容两块 —— 与上面三个字段同等对待（同一个表单、同一份「打开即重置」） */
  const [shots, setShots] = useState<TemplateShotLine[]>(initial.shots)
  const [shot, setShot] = useState(initial.shot)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** AI 两颗键各自转圈（同时点两颗时互不干扰） */
  const [aiShotsBusy, setAiShotsBusy] = useState(false)
  const [aiShotBusy, setAiShotBusy] = useState(false)
  /**
   * AI 的失败**单独一格**，不与「保存失败」混着显示。
   * 混在一起的话提示会写「保存失败：LLM 超时」—— 而那次根本没保存，
   * 用户会以为模板没存上（其实一个字都还没提交），转头去重存一遍。
   */
  const [aiError, setAiError] = useState<string | null>(null)

  /*
   * 每次打开都重置。不重置的话：上一次填了一半的名字会留在框里，
   * 而下一次打开时 `initial.name` 已经是**另一个产品**（或另一条模板）了 ——
   * 用户看着一个和自己产品无关的旧名字，很容易顺手点保存，
   * 存下来的模板名与内容对不上（而模板名是唯一能让他在一堆卡里找到它的东西）。
   *
   * ⚠️ 依赖必须是**五个标量**，不能写 `[open, initial]` —— `initial` 是调用方
   * 每次渲染新建的对象，那样这个 effect 会在每次按键后重跑，把用户正在打的字
   * 冲回初值（表现是「输入框打不进字」）。
   * `initial.shots` 是数组、同样每次新建，所以取它的 **length** 当指纹：
   * 长度一变必然要重置，而「弹窗开着的时候换了一批同长度的分镜」在现实里不会发生
   * （弹窗挡着页面，用户在弹窗里也改不了镜头数 —— 见 `TemplateShotLine`）。
   */
  useEffect(() => {
    if (!open) return
    setName(initial.name)
    setTag(initial.tag)
    setDescription(initial.description)
    /* 深拷一层：直接把 `initial.shots` 的引用收进 state 的话，
       下面改某个镜头就会**原地改到调用方那份数组**（工作台的 `storyboard`），
       表现是「在弹窗里改了描述，但没点保存就关掉，页面上的分镜也变了」。 */
    setShots(initial.shots.map((s) => ({ ...s })))
    setShot(initial.shot)
    setError(null)
    setAiError(null)
  }, [open, initial.name, initial.tag, initial.description, initial.shots.length, initial.shot])

  const runAiShots = async () => {
    if (!onAiShots || aiShotsBusy || !shots.length) return
    setAiShotsBusy(true)
    setAiError(null)
    try {
      // 段数与长度校验都在回调里（它才知道当前该有几段）；这里只管把结果收下
      setShots(await onAiShots(shots))
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e))
    } finally {
      setAiShotsBusy(false)
    }
  }

  const runAiShot = async () => {
    if (!onAiShot || aiShotBusy) return
    setAiShotBusy(true)
    setAiError(null)
    try {
      setShot(await onAiShot(shot))
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e))
    } finally {
      setAiShotBusy(false)
    }
  }

  const submit = async () => {
    const clean = name.trim()
    if (!clean) {
      setError('给模板起个名字 —— 它是你在模板条里认出这条的唯一线索')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit({
        name: clean,
        tag: tag.trim(),
        description: description.trim(),
        shots: shots.map((s) => ({ t: s.t, d: s.d.trim() })),
        /*
         * ⚠️ 全片约束会被拼进提示词的**一行**（`templateShotLine()`），
         * 换行会把后面的段落结构顶乱 —— 所以这里把回车折成空格。
         * 不是偷偷改用户的东西：输入框下面那行提示明说了会折。
         * 反过来直接存下带换行的版本，症状是「提示词里多出一截不对齐的行」，
         * 而对着界面完全看不出来。
         */
        shot: shot.replace(/\s*\n+\s*/g, ' ').trim(),
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? '编辑模板' : '存为模板'}
      icon={isEdit ? 'edit' : 'star'}
      width={560}
      /*
       * 通栏提交栏（2026-10-08 用户口径：「**模板保存按钮不够明显**」）。
       *
       * 原来它与「取消」并排挤在右下角、两颗一样大 —— 而在这个弹窗里保存是
       * **唯一的主操作**（取消还有右上角的 × 与 Esc 两条路）。所以主键单独占满
       * 一行、抬到 42px 高，取消退到它下面：**主次靠面积拉开，不靠颜色深浅** ——
       * 加饱和度会让同一套主题里出现两种「主按钮」，那是视觉上的第二真源。
       */
      footer={
        <div className="savetpl__foot">
          <Button variant="primary" size="lg" icon="check" onClick={submit} loading={busy} block>
            {isEdit ? '保存' : '保存模板'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={busy} block>
            取消
          </Button>
        </div>
      }
    >
      <div className="stack">
        {isEdit ? (
          <div className="text-xs muted">
            改的是名字、角标、说明，以及模板内容（每一镜拍什么 / 全片约束）。
            参数与镜头时间轴不在这里动 —— 想换一套参数，回工作台调好再存一条新的。
          </div>
        ) : (
          <div className="text-xs muted">
            存下来的是这套结构：五个参数 + 分镜脚本 + 全片约束。
            下次换一个产品套用它，提示词会按新产品重新拼 —— 所以模板能一直用下去。
          </div>
        )}

        <div className="savetpl">
          <div className="savetpl__cover">
            {coverUrl ? (
              <SmartImage src={coverUrl} alt="模板封面" />
            ) : (
              <div className="savetpl__nocover">
                还没有分镜图
                <span className="text-xs muted">卡片会先用一张空图代替，之后出过分镜图可以再来存一条</span>
              </div>
            )}
          </div>
          <div className="grow" style={{ minWidth: 0 }}>
            <Field label="模板名" hint="卡面上显示的就是它 —— 你在一排卡里靠它认人">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：我的 4 LOOK 闪切" />
            </Field>
            <Field label="角标（可选）" hint="填了就显示在名字前面。留空只显示名字">
              <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="例如：服装（建议两个字，卡片很窄）" />
            </Field>
            <Field label="一句说明（可选）" hint="不填就用默认的一句；悬停卡片时会看到">
              <Textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="这套模板适合什么品、拍出来是什么节奏"
              />
            </Field>
          </div>
        </div>

        {/* ------------------------------------------------------------------
            模板内容（2026-10-08 新增）—— 套用这条模板时，提示词里的镜头与
            约束就是这两块。摆在这里而不是折叠起来：它们正是「这条模板值不值得
            存」的核心，默认藏起来等于让用户以为存下的只是那几个参数。
           ------------------------------------------------------------------ */}

        <div className="savetpl__block">
          <div className="savetpl__blockhead">
            <div className="grow">
              <div className="savetpl__blocktitle">
                分镜脚本
                <span className="muted"> · {shots.length} 个镜头</span>
              </div>
              <div className="text-xs muted">
                每段拍什么，可以逐条改。时间位跟着成片时长走，不在这里改。
              </div>
            </div>
            {onAiShots ? (
              <Button
                variant="outline"
                size="sm"
                icon="sparkles"
                onClick={() => void runAiShots()}
                loading={aiShotsBusy}
                disabled={!shots.length || aiShotBusy}
                title={
                  shots.length
                    ? '让 AI 把每一段的画面描述写到「照着就能拍」的程度（只改描述，不改段数与时间位）'
                    : '还没有分镜 —— 先回工作台出一版分镜图，再来改'
                }
              >
                AI 扩写
              </Button>
            ) : null}
          </div>
          {shots.length ? (
            <div className="savetpl__shots">
              {shots.map((s, i) => (
                /* key 用下标：镜头**不会重排**（时间位只读、也不能增删），
                   所以下标就是稳定身份。用 `s.t` 当 key 反而会在时间位变化时
                   把整行连同光标一起重建。 */
                <div className="savetpl__shot" key={i}>
                  <div className="savetpl__shottime">{s.t}</div>
                  <Textarea
                    rows={2}
                    value={s.d}
                    onChange={(e) =>
                      setShots(shots.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)))
                    }
                    placeholder="这一段画面拍什么"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="savetpl__noShot text-xs muted">
              这套参数还没有分镜脚本，套用时会按通用三段式铺开 —— 先回工作台出一版分镜图，再来改成你自己的。
            </div>
          )}
        </div>

        <div className="savetpl__block">
          <div className="savetpl__blockhead">
            <div className="grow">
              <div className="savetpl__blocktitle">全片约束</div>
              <div className="text-xs muted">
                跨镜头的硬要求（如「转速全程恒定」「两次变装只换服装」）。
                会拼进提示词的【补充】段；留空则不带这一行。
              </div>
            </div>
            {onAiShot ? (
              <Button
                variant="outline"
                size="sm"
                icon="sparkles"
                onClick={() => void runAiShot()}
                loading={aiShotBusy}
                disabled={aiShotsBusy}
                title="让 AI 把这几条约束写全（每一条都可验收）—— 留空也能点，它会从这支片子的参数出发写一版"
              >
                AI 扩写
              </Button>
            ) : null}
          </div>
          <Textarea
            rows={3}
            value={shot}
            onChange={(e) => setShot(e.target.value)}
            placeholder="例如：转速全程恒定；两次变装只换服装；不要黑帧"
          />
          {/* 折行这件事必须**写在界面上**：它是代码在提交时做的唯一一处改写，
              不说就等于偷偷动用户输入（见 `submit` 里那段）。 */}
          <div className="text-xs muted">回车会折成空格 —— 这一行要拼进提示词里同一行。</div>
        </div>

        {/* 核对区：新建时用来核对「存的是不是他现在这一套」，错了要能当场看出来。
            编辑时不渲染 —— 见上面 `detail` 的注释。 */}
        {detail ? (
          <div className="savetpl__facts">
            <div>
              <span className="muted">参数</span> {detail.summary}
            </div>
            <div>
              <span className="muted">分镜</span>{' '}
              {shots.length ? `${shots.length} 个镜头（已随模板存下）` : '还没有分镜，会存成通用三段式'}
            </div>
            <div>
              <span className="muted">封面</span>{' '}
              {coverUrl ? '用这张 3×3 分镜图（已转存，不会过期）' : '暂无分镜图'}
            </div>
            <div>
              <span className="muted">内容</span>{' '}
              {detail.hasSample
                ? '带上本页最近这条同比例的成片，卡片上的播放键播它'
                : '本页还没有同比例的成片，播放键会逐格播分镜'}
            </div>
          </div>
        ) : null}

        {aiError ? (
          /* AI 的失败与保存失败分开报（理由见 `aiError` 的注释）。同样取
             `--destructive` —— `--danger` 在本项目主题里**没有定义**。 */
          <div className="text-xs" style={{ color: 'hsl(var(--destructive))' }}>
            AI 扩写失败：{aiError}
          </div>
        ) : null}

        {error ? (
          <div className="text-xs" style={{ color: 'hsl(var(--destructive))' }}>
            保存失败：{error}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
